import { createSelector } from 'reselect';
import { v4 as uuidv4 } from 'uuid';
import { ApplicationState } from '../types';
import {
  getTraditionalOrRolloverIraFunds,
  getRothIraFunds,
} from '../funds/UserFunds/userFunds.selectors';
import { getAllFundsByTicker } from '../funds/AllFunds/allFunds.selectors';
import { getUserRegistrationTypeList } from '../user/UserRegistrationTypes/UserRegistrationTypes.selectors';
import { getRedeemThresholds } from '../redeemThresholds/redeemThresholds.selectors';
import {
  ROTH_CONVERSION_DEFAULT_MIN,
  ROTH_CONVERSION_DEFAULT_MAX,
  ROTH_CONVERSION_ALLOCATION_TYPES,
  UNIT,
  ROTH_CONVERSION_TAX_MAX_P,
  ROTH_CONVERSION_TAX_MIN_P,
  ROTH_CONVERSION_BENEFICIARY_OPTIONS,
  ROTH_CONVERSION_TAX_OPTIONS,
  ROTH_CONVERSION_NEW_DEFAULT_MIN,
} from '../../../settings/settings';
import { coalesce } from '../../../utils/utils';
import {
  getRothConversionFundLimitMessage,
  getRothFundValidationMessage,
  getRothIRAFundValidationMessage,
  setFundPendingTransaction,
} from 'src/utils/funds/funds-utility';
import {
  validateRothConversionFund,
  validateRothConversionIRAFund,
  validateRothBeneficiary,
} from '../../../utils/form/validate/validate';
import { UserRegistration } from '../user/UserRegistrationTypes/types';
import { RothFund } from './types';
import { updateRothConversionFundNotesForFund } from '../../../utils/funds/rothConversion/roth-conversion-fund-notes-utility';
import {
  getExchangePendingTransactionRecordsByAgreement,
  getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
  getSellPendingTransactionRecordsByAgreement,
} from '../pendingTransactions/pendingTransactions.selectors';
import { getUserBPID } from '../user/PersonalInformation/personalInformation.selectors';
import { getUserRetirementFunds } from '../funds/UserFunds/userFunds.selectors';

export const getRothConversionSubState = ({
  rothConversionTransaction,
}: ApplicationState) => rothConversionTransaction;

/**
 * Get Converted from Funds that user interacted with (step 1)
 * @return {RothFund[]}
 */
export const getRothConversionFromLookup = createSelector(
  [getRothConversionSubState],
  (state) => state.convertFromFunds
);

/**
 * Gets full list of funds that can be converted from (populates step 1)
 *
 * @function
 * @param state
 * @returns {ExchangeFund[]}
 */
export const getRothConversionFromFundsList = createSelector(
  [
    getAllFundsByTicker,
    getTraditionalOrRolloverIraFunds,
    getRothConversionFromLookup,
    getRedeemThresholds,
    getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    getExchangePendingTransactionRecordsByAgreement,
    getSellPendingTransactionRecordsByAgreement,
    getUserRetirementFunds,
    getRothIraFunds,
  ],
  (
    getAllFundsByTicker,
    rollOverFunds,
    convertFromFunds,
    redeemThresholds: Record<string, number>,
    pendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    pendingExchangeTransactions,
    pendingSellTransactions,
    { ROTH_IRA = [] },
    existingRothIraFunds
  ) => {
    return rollOverFunds.map((fund) => {
      const fundLookup = convertFromFunds[fund.agreementID] || {};
      const allFundInfoLookup = getAllFundsByTicker[fund.ticker];
      const redeemableThresholdDollarPercentage = coalesce(
        redeemThresholds[fund.trpCode],
        100
      );
      let validationProps = {};
      const isNewFund = !existingRothIraFunds.some(
        (existingFund) => existingFund.ticker === fund.ticker
      );
      const fundWithData = {
        ...fund,
        ...fundLookup,
        ...allFundInfoLookup,
        // may need an api call to get transaction limits for account
        transactionLimit: {
          minimum: isNewFund
            ? ROTH_CONVERSION_NEW_DEFAULT_MIN
            : ROTH_CONVERSION_DEFAULT_MIN,
          maximum: ROTH_CONVERSION_DEFAULT_MAX,
        },
        notes: {},
        hasUncollectedShares: fund.balanceAsOf !== fund.equityAsOf,
        sharePrice: allFundInfoLookup?.price?.price,
        redeemableThresholdDollarPercentage,
        redeemableFundDollars:
          redeemableThresholdDollarPercentage === 100
            ? fund.balanceAsOf
            : Math.round(
                redeemableThresholdDollarPercentage * fund.balanceAsOf
              ) / 100,
      };
      // validation
      if (fundLookup.grossAmount) {
        validationProps = {
          errorMessage: getRothFundValidationMessage(fundWithData),
          error: !validateRothConversionFund(
            fundWithData,
            existingRothIraFunds
          ),
        };
      }
      setFundPendingTransaction(
        fundWithData,
        pendingBuyExchangeSellTransactionRecordsByAgreementGrouped
      );

      //takes the agreementID od all sell and exchange transactions
      // to check if they are From exchange / sell fund
      // and block them so the user cannot add more transactions to it
      const exchangeAgreementIDs = Object.keys(pendingExchangeTransactions);
      const sellAgreementIDsObject = Object.keys(pendingSellTransactions);
      const sellAndExchange = Object.assign(
        exchangeAgreementIDs,
        sellAgreementIDsObject
      );
      const hasRothSameFundCode = ROTH_IRA.find(
        (el) => el.fundName === fund.fundName && el.purchaseBlocked
      );
      const hasRothSameFundCodeStopPurchase = ROTH_IRA.find(
        (el) =>
          el.fundName === fund.fundName &&
          el?.fundLock?.['fundLocked'] &&
          el?.fundLock?.foundFeatures?.some(
            (data) => data.code === 'Stop Purchase'
          )
      );
      const isIClass = ROTH_IRA.find(
        (el) =>
          el.fundType === 'ICLASS' &&
          fund.fundType !== 'ICLASS' &&
          `${fund.fundName} - I Class` === el.fundName
      );
      updateRothConversionFundNotesForFund(fundWithData, sellAndExchange, {
        hasRothSameFundCode:
          hasRothSameFundCode &&
          hasRothSameFundCode?.fundName === fund?.fundName,

        hasRothSameFundCodeStopPurchase:
          hasRothSameFundCodeStopPurchase &&
          hasRothSameFundCodeStopPurchase?.fundName === fund?.fundName,
        isIClass,
      });

      return {
        ...fundWithData,
        ...validationProps,
      };
    });
  }
);

export const getRothConversionAllocationType = createSelector(
  [getRothConversionSubState],
  (state) =>
    (state.allocationType = ROTH_CONVERSION_ALLOCATION_TYPES.SAME_FUNDS)
);

export const getRothConversionTaxOption = createSelector(
  [getRothConversionSubState],
  (state) => state.taxOption
);

export const getRothConversionTaxPercentageCheck = createSelector(
  [getRothConversionSubState],
  (state) =>
    state.taxPercentage &&
    state.taxPercentage >= ROTH_CONVERSION_TAX_MIN_P &&
    state.taxPercentage <= ROTH_CONVERSION_TAX_MAX_P
);

export const getRothConversionTaxPercentage = createSelector(
  [getRothConversionSubState],
  (state) => {
    if (state.taxOption === ROTH_CONVERSION_TAX_OPTIONS.NO_DONT_WITHHOLD) {
      return (state.taxPercentage = 0);
    }
    return state.taxPercentage;
  }
);

export const getRothConversionTermsConditions = createSelector(
  [getRothConversionSubState],
  (state) => state.termsConditions
);

/**
 * Loops through all Selected funds and sums up amount in dollars
 * @function
 * @param state
 * @returns {number}
 */
export const getRothConversionFromFundsAmount = createSelector(
  [getRothConversionSubState],
  (state) =>
    Object.keys(state.convertFromFunds).reduce((total, currID) => {
      return total + (state.convertFromFunds[currID].grossAmountDollars || 0);
    }, 0)
);

/**
 * Checks to make sure every individual from fund meets the min/max totals
 * Also makes sure that at least one fund is selected
 * @function
 * @param state
 * @returns {boolean}
 */
export const getMinMaxFromFundsCheck = createSelector(
  [getRothConversionSubState],
  (state) => {
    const fundIds = Object.keys(state.convertFromFunds);
    return (
      fundIds.length &&
      fundIds.every((key) => {
        switch (state.convertFromFunds[key].grossUnit) {
          case 'DOLLAR':
            return (
              state.convertFromFunds[key].grossAmountDollars &&
              (state.convertFromFunds[key].grossAmountDollars || 0) >=
                ROTH_CONVERSION_DEFAULT_MIN &&
              (state.convertFromFunds[key].grossAmountDollars || 0) <=
                ROTH_CONVERSION_DEFAULT_MAX
            );
          case 'PERCENT':
            return (
              state.convertFromFunds[key].grossAmount &&
              (state.convertFromFunds[key].grossAmount || 101) <= 100 &&
              (state.convertFromFunds[key].grossAmountDollars || 0) >=
                ROTH_CONVERSION_DEFAULT_MIN &&
              (state.convertFromFunds[key].grossAmountDollars || 0) <=
                ROTH_CONVERSION_DEFAULT_MAX
            );
          case 'SHARES':
            return (
              (state.convertFromFunds[key].grossAmountDollars || 0) >=
                ROTH_CONVERSION_DEFAULT_MIN &&
              (state.convertFromFunds[key].grossAmountDollars || 0) <=
                ROTH_CONVERSION_DEFAULT_MAX &&
              state.convertFromFunds[key].grossAmountDollars
            );
          case 'ALLSHARES':
            return (
              (state.convertFromFunds[key].grossAmountDollars || 0) >=
                ROTH_CONVERSION_DEFAULT_MIN &&
              (state.convertFromFunds[key].grossAmountDollars || 0) <=
                ROTH_CONVERSION_DEFAULT_MAX &&
              state.convertFromFunds[key].grossAmountDollars
            );
          default:
            return false;
        }
      })
    );
  }
);

/**
 * Checks to make sure every individual To fund meets the min/max totals
 * Also makes sure that at least one fund is selected
 * @function
 * @param state
 * @returns {boolean}
 */
export const getMinMaxToFundsCheck = createSelector(
  [getRothConversionSubState],
  (state) => {
    const fundIds = Object.keys(state.allocateToFunds);
    return (
      fundIds.length &&
      fundIds.every(
        (key) =>
          state.allocateToFunds[key].grossAmountDollars &&
          (state.allocateToFunds[key].grossAmountDollars || 0) >=
            ROTH_CONVERSION_DEFAULT_MIN &&
          (state.allocateToFunds[key].grossAmountDollars || 0) <=
            ROTH_CONVERSION_DEFAULT_MAX
      )
    );
  }
);
/**
 * creates an array of all the user selected From funds
 * and returns th funds names and amounts for the step 1 summary
 * @function
 * @param state
 * @returns {array}
 */
export const getRothConversionFromAsArray = createSelector(
  [getRothConversionSubState],
  (state) => {
    return sortFundsByNewandName(Object.values(state.convertFromFunds));
  }
);

export const getRothConversionFundTypeNotDollar = createSelector(
  [getRothConversionFromAsArray],
  (fromFunds) => {
    return fromFunds.some((fund) => fund.grossUnit !== 'DOLLAR');
  }
);
/**
 * Get Allocated to Funds that user interacted with (step 2)
 * Will be drawn from funds in step 1 and based on user allocation selection
 * @return {RothFund}
 */
const getRothAllocateToLookup = createSelector(
  [getRothConversionSubState],
  (state) => state.allocateToFunds
);

/**
 * Will sort an array by new and name with priority in that order
 * @param funds {Fund[]}
 * @returns {Fund[]}
 */
const sortFundsByNewandName = (funds) => {
  return funds.sort((a, b) => {
    // prioritize sorting by whther or not its a newFunds
    // if (a.newFund > b.newFund) return 1;
    // if (a.newFund < b.newFund) return -1;
    // then sort alphabetically
    if (a.fundName > b.fundName) {
      return 1;
    }
    if (a.fundName < b.fundName) {
      return -1;
    }

    return 1;
  });
};

/**
 * Get Allocated to Funds that user interacted with (step 2)
 * Will be drawn from funds in step 1 and based on user allocation selection
 * @return {RothFund}
 */
export const getRothAllocateToAsArray = createSelector(
  [getRothAllocateToLookup],
  (allocateToFunds) =>
    sortFundsByNewandName(
      Object.keys(allocateToFunds).map((agreementID) => ({
        ...allocateToFunds[agreementID],
        agreementID: agreementID,
      }))
    )
);

/**
 * Returns all funds user manually added in step 2
 * @return {RothFund[]}
 */
const getUserAddedRothFundsList = createSelector(
  [getRothConversionSubState],
  (state) => state.userAddedFunds
);

/**
 * Will convert funds from step 1 to roth ira forms, looking to merge
 * infor from existing roth ira if it exists
 * @return {Fund[]}
 */
const allFundsConvertedToRothIRA = createSelector(
  [
    getAllFundsByTicker,
    getRothIraFunds,
    getRothConversionFromAsArray,
    getUserAddedRothFundsList,
    getUserRegistrationTypeList,
    getRothConversionAllocationType,
  ],
  (
    getAllFundsByTicker,
    rothIraFunds,
    convertFromFunds: RothFund[],
    userAddedFunds: RothFund[] = [],
    userRegistrationTypes: UserRegistration[],
    allocationType
  ) => {
    // find user info for their roth ira agreements if it exists, if not use traditional
    const search = rothIraFunds.length ? 'ROTH_IRA' : 'TRADITIONAL_IRA';
    const userRegistration = userRegistrationTypes.find(
      (r) => r.agreementType === search
    );

    const fundsToConvert = convertFromFunds;

    // merge step 1 funds to be converted and user added funds in step 2
    // then get all fund display info for them
    return fundsToConvert.concat(userAddedFunds).map((fund) => {
      // get all general info on fund from allfunds
      const allFundInfoLookup = getAllFundsByTicker[fund.ticker];

      // search for the fund in list of existing roth-ira funds under the user
      const existingRothIraFund = rothIraFunds.find(
        (f) => f.ticker === fund.ticker
      );

      const newFund = !existingRothIraFund;
      const defaultRothIRAProps = {
        agreementType: 'ROTH_IRA',
        displayClassification: 'RETIREMENT',
      };

      const fundWithData = {
        ...allFundInfoLookup,
        // may need an api call to get transaction limits for account
        transactionLimit: {
          minimum: ROTH_CONVERSION_DEFAULT_MIN,
          maximum: ROTH_CONVERSION_DEFAULT_MAX,
        },
        fundName: allFundInfoLookup.name,
        sharePrice: allFundInfoLookup?.price?.price,
        fundLimitMessage: getRothConversionFundLimitMessage(fund, rothIraFunds),
        grossAmount: fund.grossAmountDollars,
        grossAmountDollars: fund.grossAmountDollars,
        grossUnit: UNIT.DOLLARS, // default unit
        newFund: newFund,
        agreementID: existingRothIraFund?.agreementID,
        formattedEntityNames: userRegistration?.formattedEntityNames,
        ...(newFund ? defaultRothIRAProps : existingRothIraFund),
      };

      return fundWithData;
    });
  }
);

/**
 * Gets all funds selected in convertedFromFunds and returns RothIRA versions of them for step2
 * will have different validation
 * @return {Funds[]}
 */
export const getRothAllocateToFundsList = createSelector(
  [
    allFundsConvertedToRothIRA,
    getRothAllocateToAsArray,
    getRothConversionFromFundsAmount,
    getRothIraFunds,
  ],
  (
    convertedFunds,
    allocateToFundsList,
    fromFundsTotal,
    existingRothIraFunds
  ) => {
    let totalAllFunds = 0;

    // gets all converted funds and compare with step 2 user allocations
    const rothIRAFunds = (convertedFunds as Array<any>).map((fund) => {
      const allocatedFund = allocateToFundsList.find(
        (f) => f.ticker === fund.ticker
      );
      // if the converted fund is a new fund that has not been saved yet,
      // create a new agreement id to track
      const agreementID =
        allocatedFund?.agreementID || fund.agreementID || uuidv4();

      totalAllFunds =
        totalAllFunds +
        (allocatedFund?.selected
          ? allocatedFund?.grossAmountDollars || fund.grossAmountDollars
          : 0);

      return {
        ...fund,
        ...allocatedFund,
        agreementID,
      };
    });

    // validate after getting all general fund information
    return sortFundsByNewandName(
      rothIRAFunds.map((f) => ({
        ...f,
        totalAvailable: fromFundsTotal,
        ...(f.grossAmountDollars !== undefined &&
          f.selected && {
            error:
              !validateRothConversionIRAFund(f, existingRothIraFunds) ||
              totalAllFunds !== fromFundsTotal,
            errorMessage: getRothIRAFundValidationMessage(
              f,
              totalAllFunds,
              fromFundsTotal
            ),
          }),
      }))
    );
  }
);

// Gets beneficiary option
export const getRothBeneficiaryOption = createSelector(
  [getRothConversionSubState],
  (state) => state.beneficiaryOption
);

/**
 * Return user added Beneficiaries state as a object with two lists
 * @return { primary: Beneficiary[], secondary: Beneficiary[] }
 */
export const getUserAddedRothBeneficiariesAsLists = createSelector(
  [getRothConversionSubState],
  (state) => {
    const {
      userAddedBeneficiaries: { primary, secondary },
    } = state;

    let primaryTotal = 0;
    let secondaryTotal = 0;

    // use id's for management purposes when user updates a beneficiary
    const primaryList = Object.keys(primary).map((id) => {
      primaryTotal = primaryTotal + (primary[id].distribution || 0);
      return {
        ...primary[id],
        ...validateRothBeneficiary(primary[id]),
        id,
      };
    });

    const secondaryList = Object.keys(secondary).map((id) => {
      secondaryTotal = secondaryTotal + (secondary[id].distribution || 0);
      return {
        ...secondary[id],
        ...validateRothBeneficiary(secondary[id]),
        id,
      };
    });

    return {
      primary: primaryList,
      secondary: secondaryList,
      percentageCheck: {
        primary: primaryTotal === 100,
        secondary: secondaryTotal === 100,
      },
    };
  }
);

/**
 * Get Validation check of user Roth beneficiaries step
 */
export const getValidateUserRothBeneficiaries = createSelector(
  [getRothBeneficiaryOption, getUserAddedRothBeneficiariesAsLists],
  (rothBeneficiaryOption, userRothBeneficiaries) => {
    if (!rothBeneficiaryOption) {
      return false;
    }

    if (
      rothBeneficiaryOption === ROTH_CONVERSION_BENEFICIARY_OPTIONS.CREATE_NEW
    ) {
      const { primary, secondary, percentageCheck } = userRothBeneficiaries;
      const validPrimaries = primary.every((b) => b.isValid);
      const validSecondaries = secondary.every((b) => b.isValid);
      const validTotalPercentages =
        percentageCheck.primary &&
        (secondary.length ? percentageCheck.secondary : true);

      return validPrimaries && validSecondaries && validTotalPercentages;
    }

    return true;
  }
);

export const getFilteredRothConversionFromFundsList = createSelector(
  [getRothConversionFromFundsList, getUserBPID],
  (fromFundsList, BPID) => {
    return fromFundsList.filter((fund) => rothConversionFundFilter(fund, BPID));
  }
);

export const getFilteredRothConversionFromFundsListOwner = createSelector(
  [getRothConversionFromFundsList, getUserBPID],
  (fromFundsList, BPID) => {
    return fromFundsList.filter((fund) =>
      rothConversionFundFilterOwner(fund, BPID)
    );
  }
);

//filters out all funds the user is not the owner of
function rothConversionFundFilterOwner(fund, BPID) {
  const { agreementParties } = fund;

  const ownerArr = agreementParties
    ? agreementParties.filter((roles) => roles.roleCode === 'OWNR')
    : [];

  return ownerArr?.length > 0 && ownerArr[0].businessPartyID === BPID
    ? true
    : false;
}

//filters out all funds that are 0 dollar funds and filters out all funds the user is not the owner of
function rothConversionFundFilter(fund, BPID) {
  const { balanceAsOf, equityAsOf, agreementParties } = fund;

  const ownerArr = agreementParties
    ? agreementParties.filter((roles) => roles.roleCode === 'OWNR')
    : [];

  return ownerArr?.length > 0 &&
    ownerArr[0].businessPartyID === BPID &&
    ((balanceAsOf > 0 && equityAsOf > 0) ||
      (equityAsOf === 0 && balanceAsOf > 0))
    ? true
    : false;
}

export const getDoesRothConversionHaveEligibleFunds = createSelector(
  [getFilteredRothConversionFromFundsList],
  (fromFundListLength) => fromFundListLength.length > 0
);

export const getFilteredRothFundsByOwner = createSelector(
  [getRothIraFunds, getUserBPID],
  (fromFundsList, BPID) => {
    return fromFundsList.filter((fund) =>
      rothConversionFundFilterOwner(fund, BPID)
    );
  }
);
